/**
 * Consumption Unlock Control
 * ═══════════════════════════
 * Admin / Super-Admin control panel for `consumption_unlocks` records.
 *
 * Location in navigation:
 *   Management → Consumption Unlock Control
 *
 * Every row corresponds to one unlock issued for a (center, window).
 * Statuses: active / expired / relocked.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Lock, Unlock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConsumptionUnlockControl() {
  const { session } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState("");
  const [status, setStatus] = useState("");   // "" | active | expired | relocked
  const [q, setQ] = useState("");             // free-text filter

  const load = useCallback(async () => {
    if (!session?.token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/consumption/unlocks/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: session.token,
          center: center || undefined,
          status: status || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed to load");
      setRows(d.unlocks || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [session?.token, center, status]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(u =>
      (u.center || "").toLowerCase().includes(term) ||
      (u.reason || "").toLowerCase().includes(term) ||
      (u.unlocked_by_name || "").toLowerCase().includes(term)
    );
  }, [rows, q]);

  const centers = useMemo(
    () => Array.from(new Set(rows.map(r => r.center))).sort(),
    [rows],
  );

  const relock = async (u) => {
    if (!window.confirm(`Relock ${u.center} · ${u.from_date}${u.to_date !== u.from_date ? ` → ${u.to_date}` : ""}?`)) return;
    try {
      const r = await fetch(`${API}/api/consumption/unlocks/relock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: session.token, unlock_id: u._id, reason: "Manual relock" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Relock failed");
      toast.success("Relocked");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const statusBadge = (s) => {
    if (s === "active") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Active</Badge>;
    if (s === "expired") return <Badge className="bg-slate-100 text-slate-600 border-slate-300">Expired</Badge>;
    if (s === "relocked") return <Badge className="bg-rose-100 text-rose-800 border-rose-300">Relocked</Badge>;
    return <Badge>{s}</Badge>;
  };
  const typeLabel = { single: "Single Date", date_range: "Date Range", month: "Month", month_range: "Month Range" };
  const fmtDT = (iso) => (iso || "").slice(0, 16).replace("T", " ");

  return (
    <div className="p-4 space-y-4" data-testid="consumption-unlock-control-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Unlock className="w-6 h-6 text-emerald-700" />
          Consumption Unlock Control
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Grant, review, and relock consumption-editing windows for Center Managers.
        </p>
      </div>

      {/* Filters */}
      <Card><CardContent className="p-3 flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-xs">Center</Label>
          <select value={center} onChange={(e) => setCenter(e.target.value)}
                  className="h-8 border rounded px-2 text-xs" data-testid="cuc-center">
            <option value="">All centers</option>
            {centers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="h-8 border rounded px-2 text-xs" data-testid="cuc-status">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="relocked">Relocked</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Center / reason / user…"
                 className="h-8 w-48 text-xs" data-testid="cuc-search" />
        </div>
        <Button size="sm" variant="outline" onClick={load} className="text-xs" data-testid="cuc-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
        <div className="ml-auto text-xs text-slate-500">
          {filtered.length} unlock{filtered.length === 1 ? "" : "s"}
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Center</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Unlocked By</TableHead>
                <TableHead>Unlocked On</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-6">
                  No unlock records match the filters.
                </TableCell></TableRow>
              ) : filtered.map(u => (
                <TableRow key={u._id} data-testid={`cuc-row-${u._id}`}>
                  <TableCell className="font-semibold">{u.center}</TableCell>
                  <TableCell>{typeLabel[u.unlock_type] || u.unlock_type}</TableCell>
                  <TableCell>{u.from_date}</TableCell>
                  <TableCell>{u.to_date}</TableCell>
                  <TableCell className="max-w-xs truncate" title={u.reason}>{u.reason}</TableCell>
                  <TableCell>{u.unlocked_by_name || u.unlocked_by}</TableCell>
                  <TableCell>{fmtDT(u.unlocked_at)}</TableCell>
                  <TableCell>{u.expires_at ? fmtDT(u.expires_at) : <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell>{statusBadge(u.status)}</TableCell>
                  <TableCell className="text-right">
                    {u.status === "active" ? (
                      <Button size="sm" variant="outline"
                              className="text-rose-700 border-rose-300 hover:bg-rose-50 text-xs"
                              onClick={() => relock(u)}
                              data-testid={`cuc-relock-${u._id}`}>
                        <Lock className="w-3 h-3 mr-1" /> Relock
                      </Button>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}
